import { useState } from 'react';
import { RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { apiClient } from '@rti/shared';

const REFRESH_ACTIONS = [
  { key: 'ACC_PE', label: 'ACC PE Data' },
  { key: 'ACC_PP', label: 'ACC PP Data' },
  { key: 'ACC_PS', label: 'ACC PS Data' },
  { key: 'ACC_PVC', label: 'ACC PVC Data' },
  { key: 'ACC_PE_FINAL', label: 'ACC PE Final Data' },
  { key: 'ACC_PP_FINAL', label: 'ACC PP Final Data' },
  { key: 'ACC_PS_FINAL', label: 'ACC PS Final Data' },
  { key: 'ACC_PVC_FINAL', label: 'ACC PVC Final Data' },
  { key: 'Capro', label: 'Capro Data' },
  { key: 'ChemOrbis', label: 'ChemOrbis Data' },
  { key: 'Chlorine', label: 'Chlorine Data' },
  { key: 'Contracts', label: 'Contracts Data' },
  { key: 'Driver', label: 'Driver Data' },
  { key: 'DriverCharts', label: 'Driver Charts Data' },
  { key: 'DriverPriceChanges', label: 'Driver Price Changes' },
  { key: 'EIA_FORECAST', label: 'EIA Forecast Data' },
  { key: 'FairPrice', label: 'Fair Price Data' },
  { key: 'Forecast', label: 'Forecast Data' },
  { key: 'ForecastCommentary', label: 'Forecast Commentary' },
  { key: 'Petchem', label: 'Petchem Data' },
  { key: 'PetMasterPricing', label: 'PetMaster Pricing Data' },
  { key: 'RefineryRates', label: 'Refinery Rates Data' },
  { key: 'DIESEL_PRICES', label: 'Diesel Prices Data' },
  { key: 'EmailBullets', label: 'Email Bullets Data' },
  { key: 'MI_ALL', label: 'Market Intel - ALL' },
  { key: 'MI_PE', label: 'Market Intel - PE' },
  { key: 'MI_PP', label: 'Market Intel - PP' },
  { key: 'MI_PS', label: 'Market Intel - PS' },
  { key: 'MI_PVC', label: 'Market Intel - PVC' },
  { key: 'MI_PET', label: 'Market Intel - PET' },
];

const RefreshDataPanel = () => {
  const [selected, setSelected] = useState<string[]>([]);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [results, setResults] = useState<Record<string, 'success' | 'error' | null>>({});

  const handleSelect = (key: string) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    setSelected(REFRESH_ACTIONS.map((a) => a.key));
  };

  const handleDeselectAll = () => {
    setSelected([]);
  };

  const responseIndicatesError = (res: any): boolean => {
    if (!res || typeof res !== 'object') return false;
    if (typeof res.detail === 'string' && res.detail.trim() !== '') return true;
    if (Array.isArray(res.errors) && res.errors.length > 0) return true;
    return false;
  };

  const handleRefresh = async (key: string) => {
    setRefreshing(key);
    setResults((prev) => ({ ...prev, [key]: null }));
    try {
      const res = await apiClient.runRefreshAction(key);
      setResults((prev) => ({
        ...prev,
        [key]: responseIndicatesError(res) ? 'error' : 'success',
      }));
    } catch {
      setResults((prev) => ({ ...prev, [key]: 'error' }));
    } finally {
      setRefreshing(null);
    }
  };

  const handleRefreshSelected = async () => {
    for (const action of REFRESH_ACTIONS) {
      if (selected.includes(action.key)) {
        await handleRefresh(action.key);
      }
    }
  };

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    setResults({});
    try {
      for (const action of REFRESH_ACTIONS) {
        await handleRefresh(action.key);
      }
    } finally {
      setRefreshingAll(false);
    }
  };

  return (
    <div className="rs-panel rs-admin-panel max-w-4xl mx-auto bg-rs-jet rounded-xl shadow-lg p-8 mt-8">
      <div className="flex items-center mb-6 gap-3">
        <RefreshCw className="h-10 w-10 text-rs-dark-green" />
        <h2 className="text-3xl font-bold ">Refresh Data</h2>
      </div>
      <p className="text-rs-off-white/50 mb-8 text-lg">Select which data sources to refresh. You can refresh individually or in bulk.</p>
      <div className="flex gap-4 mb-8">
        <button className="bg-rs-green !text-rs-off-black hover:bg-rs-dark-green  px-4 py-2 rounded transition flex items-center" onClick={handleRefreshAll} disabled={refreshingAll || !!refreshing}>
          <RefreshCw className="h-5 w-5 mr-2" /> {refreshingAll ? 'Refreshing All...' : 'Refresh All'}
        </button>
        <button className="bg-rs-salmon/50  hover:bg-rs-salmon/75  px-4 py-2 rounded transition" onClick={handleSelectAll}>Select All</button>
        <button className=" bg-rs-salmon-gradient-end  hover:bg-rs-salmon-gradient-end/70 px-4 py-2 rounded transition" onClick={handleDeselectAll}>Deselect All</button>
        <button className="bg-rs-rb-gradient-end text-white hover:bg-rs-rb-gradient-end/70 px-4 py-2 rounded transition flex items-center" onClick={handleRefreshSelected} disabled={selected.length === 0 || !!refreshing}>
          <RefreshCw className="h-5 w-5 mr-2" /> Refresh Selected
        </button>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {REFRESH_ACTIONS.map((action) => (
          <li key={action.key} className="flex items-center justify-between bg-rs-off-black rounded-lg p-4 shadow-sm">
            <label className="flex items-center gap-3 text-lg font-medium">
              <input
                type="checkbox"
                checked={selected.includes(action.key)}
                onChange={() => handleSelect(action.key)}
                disabled={!!refreshing}
                className="form-checkbox h-5 w-5 text-rs-dark-green"
              />
              {action.label}
            </label>
            <div className="flex items-center gap-2">
              <button
                className="bg-rs-dark-green text-rs-off-black hover:bg-rs-dark-green/70 px-3 py-1 rounded transition flex items-center text-sm"
                onClick={() => handleRefresh(action.key)}
                disabled={!!refreshing}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                {refreshing === action.key ? 'Refreshing...' : 'Refresh'}
              </button>
              {results[action.key] === 'success' && <CheckCircle className="h-5 w-5 text-green-500" />}
              {results[action.key] === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RefreshDataPanel;
